import type { Writable } from "svelte/store";
import { FS } from "./fs";
import type { PremiumizeConfig } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { throwIf } from "./util";

// https://www.premiumize.me/api
// The premiumize.me API answers other origins, so the browser calls it
// directly and the API key never reaches our server. The calls follow rclone's
// premiumizeme backend.
//
// The API takes no request headers from other origins, so the key cannot go
// in one. It goes in the address of every call, as in rclone, which is why
// errors must not quote addresses.
//
// premiumize.me knows folders and files by id and has no call that takes a
// path. A path is walked one folder at a time and the folder ids we learn are
// remembered. Names are case-insensitive. It keeps one time for a file, the
// time it was stored.

export const premiumizeAPI = "https://www.premiumize.me/api";

const rootID = "0";
const maxNameLen = 255;

export const prop_premiumize_mime_type = "premiumize_mime_type";

export interface PremiumizeItem {
  id?: string;
  name?: string;
  type?: string;
  size?: number;
  // seconds since epoch
  created_at?: number;
  // where the content of a file is: all it takes to download it
  link?: string;
  mime_type?: string;
}

class PremiumizeError extends Error {
  // the key was refused
  auth: boolean;
  constructor(message: string, auth: boolean) {
    super(message);
    this.auth = auth;
  }
}

function splitPath(path: string): string[] {
  return path.split("/").filter((s) => s != "");
}

function dirKey(parts: string[]): string {
  return parts.join("/").toLowerCase();
}

function sameName(a: string | undefined, b: string): boolean {
  return (a || "").toLowerCase() == b.toLowerCase();
}

function isFolder(it: PremiumizeItem): boolean {
  return it.type == "folder";
}

function findItem(items: PremiumizeItem[], name: string, folder: boolean): PremiumizeItem | undefined {
  return items.find((it) => it.id && sameName(it.name, name) && it.type == (folder ? "folder" : "file"));
}

function randomSuffix(): string {
  let a = new Uint8Array(6);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, "0")).join("");
}

// Refuses the names rclone does not send to premiumize.me as they are.
export function premiumizeCheckName(name: string): void {
  throwIf(name == "", "missing name");
  throwIf(name.length > maxNameLen, `a name cannot be longer than ${maxNameLen} characters`);
  let bad = /["\\\x00-\x1f\x7f]/.test(name) || name == "." || name == "..";
  throwIf(bad, 'a name cannot contain " or \\');
}

class FileSystemPremiumize extends FS {
  supportsDirRename = true;

  // folder ids by lower-case path, without slashes at the ends
  dirIDs = new Map<string, string>();

  constructor(config: PremiumizeConfig) {
    super(config);
  }

  get key(): string {
    return (this.config as PremiumizeConfig).apiKey;
  }

  async list(folderID: string): Promise<PremiumizeItem[]> {
    let params: Record<string, string> = { includebreadcrumbs: "false" };
    if (folderID != rootID) {
      params.id = folderID;
    }
    let js = await pmCall(this.key, "GET", "/folder/list", params);
    return Array.isArray(js.content) ? (js.content as PremiumizeItem[]) : [];
  }

  // Remembers the folders of the folder at parts, all of them: what we knew
  // about folders that are gone or were replaced is forgotten.
  remember(parts: string[], items: PremiumizeItem[]) {
    let prefix = parts.length > 0 ? dirKey(parts) + "/" : "";
    let fresh = new Map<string, string>();
    for (let it of items) {
      if (isFolder(it) && it.id && it.name && !fresh.has(it.name.toLowerCase())) {
        fresh.set(it.name.toLowerCase(), it.id);
      }
    }
    let old = new Map(this.dirIDs);
    for (let k of old.keys()) {
      if (!k.startsWith(prefix)) {
        continue;
      }
      // the folder of this listing that k is, or is under
      let child = k.substring(prefix.length).split("/")[0];
      if (fresh.get(child) != old.get(prefix + child)) {
        this.dirIDs.delete(k);
      }
    }
    for (let [name, id] of fresh) {
      this.dirIDs.set(prefix + name, id);
    }
  }

  async mkdir(parentID: string, name: string): Promise<string> {
    premiumizeCheckName(name);
    let js = await pmCall(this.key, "POST", "/folder/create", { name: name, parent_id: parentID });
    throwIf(!js.id, "premiumize.me did not create the folder");
    return js.id as string;
  }

  // Finds the id of the folder at parts. With create it makes the folders
  // that are missing.
  async resolveDir(parts: string[], create: boolean): Promise<string> {
    let id = rootID;
    // start at the deepest folder we remember
    let start = 0;
    for (let n = parts.length; n > 0; n--) {
      let known = this.dirIDs.get(dirKey(parts.slice(0, n)));
      if (known) {
        id = known;
        start = n;
        break;
      }
    }
    for (let i = start; i < parts.length; i++) {
      let items = await this.list(id);
      this.remember(parts.slice(0, i), items);
      let found = findItem(items, parts[i], true);
      if (found) {
        id = found.id!;
        continue;
      }
      throwIf(!create, `folder not found: /${parts.slice(0, i + 1).join("/")}`);
      id = await this.mkdir(id, parts[i]);
      this.dirIDs.set(dirKey(parts.slice(0, i + 1)), id);
    }
    return id;
  }

  // Lists the folder that holds the entry at parts.
  async parentOf(parts: string[], create: boolean): Promise<{ id: string; items: PremiumizeItem[] }> {
    let dir = parts.slice(0, -1);
    let id = await this.resolveDir(dir, create);
    let items = await this.list(id);
    this.remember(dir, items);
    return { id, items };
  }

  // A folder id we remember may be of a folder that is gone. When
  // premiumize.me refuses a call, forget the ids and try once more from the
  // top.
  async fresh<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (e) {
      let refused = e instanceof PremiumizeError && !e.auth;
      if (!refused || this.dirIDs.size == 0) {
        throw e;
      }
      this.dirIDs.clear();
      return await fn();
    }
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let parts = splitPath(dirPath);
      let items = await this.fresh(async () => this.list(await this.resolveDir(parts, false)));
      this.remember(parts, items);
      return FsDir.fromWire(dirPath, premiumizeItemsToWire(items));
    });
  }

  async findFile(path: string): Promise<PremiumizeItem> {
    let parts = splitPath(path);
    throwIf(parts.length == 0, "missing file name");
    return this.fresh(async () => {
      let parent = await this.parentOf(parts, false);
      let f = findItem(parent.items, parts[parts.length - 1], false);
      throwIf(!f, `file not found: ${path}`);
      return f!;
    });
  }

  // Not a link to share: whoever has it can read the file. It is used for
  // preview and download in this browser only.
  async getPublicURL(path: string): Promise<string> {
    let f = await this.findFile(path);
    throwIf(!f.link || !isHttps(f.link), "premiumize.me gave no address for the file");
    return f.link!;
  }

  renameItem(id: string, isDir: boolean, name: string): Promise<any> {
    return pmCall(this.key, "POST", isDir ? "/folder/rename" : "/item/rename", { id: id, name: name });
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let parts = splitPath(path);
    throwIf(parts.length == 0 || path.endsWith("/"), "missing file name");
    let name = parts[parts.length - 1];
    premiumizeCheckName(name);

    let target = await this.fresh(async () => {
      let parent = await this.parentOf(parts, true);
      let info = await pmCall(this.key, "POST", "/folder/uploadinfo", { id: parent.id });
      return { folderID: parent.id, old: findItem(parent.items, name, false), info };
    });
    let uploadURL = target.info.url as string;
    let uploadToken = target.info.token as string;
    throwIf(!uploadURL || !uploadToken || !isHttps(uploadURL), "premiumize.me did not open the upload");

    // As in rclone: a file with the name is moved out of the way, or the
    // upload can fail. It is removed when the new one is stored and gets its
    // name back when it is not.
    let old = target.old;
    if (old) {
      await this.renameItem(old.id!, false, `${name}.${randomSuffix()}`);
    }
    try {
      // the upload server cuts a name at the first ";"
      let uploadName = name.includes(";") ? `filerion-upload-${randomSuffix()}` : name;
      let form = new FormData();
      form.append("token", uploadToken);
      // the name of the path, not the one the file came with
      form.append("file", new File([file], uploadName, { type: file.type }));
      let js = await pmSendFile(uploadURL, form, (loaded, durMs) => {
        httpProgress(Math.min(loaded, file.size), file.size, durMs);
      });
      if (uploadName != name) {
        let id = js.id as string | undefined;
        if (!id) {
          id = findItem(await this.list(target.folderID), uploadName, false)?.id;
        }
        throwIf(!id, "premiumize.me did not store the file");
        await this.renameItem(id!, false, name);
      }
    } catch (e) {
      if (old) {
        try {
          await this.renameItem(old.id!, false, old.name!);
        } catch {
          // the first error says more
        }
      }
      throw e;
    }
    if (old) {
      await pmCall(this.key, "POST", "/item/delete", { id: old.id! });
    }
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let isDir = path.endsWith("/");
    let from = splitPath(path);
    let to = splitPath(newPath);
    throwIf(from.length == 0 || to.length == 0, "cannot rename the root folder");
    let fromName = from[from.length - 1];
    let toName = to[to.length - 1];
    premiumizeCheckName(toName);
    throwIf(isDir && (dirKey(to) + "/").startsWith(dirKey(from) + "/"), "cannot move a folder into itself");
    let sameParent = dirKey(from.slice(0, -1)) == dirKey(to.slice(0, -1));

    try {
      await this.fresh(async () => {
        let src = await this.parentOf(from, false);
        let dst = sameParent ? src : await this.parentOf(to, false);
        let id = findItem(src.items, fromName, isDir)?.id;
        throwIf(!id, `not found: ${path}`);
        // a rename that only changes the case finds itself
        let taken = dst.items.find((it) => sameName(it.name, toName) && it.id != id);
        throwIf(!!taken, `${toName} already exists`);

        let newName = fromName != toName;
        if (newName && !sameParent) {
          // as in rclone: so that the move cannot land on an entry that has
          // the old name
          await this.renameItem(id!, isDir, `${toName}.${randomSuffix()}`);
        }
        if (!sameParent) {
          await pmCall(this.key, "POST", "/folder/paste", {
            id: dst.id,
            "items[0][id]": id!,
            "items[0][type]": isDir ? "folder" : "file",
          });
        }
        if (newName) {
          await this.renameItem(id!, isDir, toName);
        }
      });
    } finally {
      if (isDir) {
        // ids stay but the paths we remember them by changed
        this.dirIDs.clear();
      }
    }
  }

  async createDirectory(dirPath: string): Promise<void> {
    let parts = splitPath(dirPath);
    throwIf(parts.length == 0, "missing directory name");
    // so that its parent is asked, not our memory
    this.dirIDs.delete(dirKey(parts));
    await this.fresh(() => this.resolveDir(parts, true));
  }

  // A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    let parts = splitPath(path);
    if (!path.endsWith("/")) {
      let f = await this.findFile(path);
      await pmCall(this.key, "POST", "/item/delete", { id: f.id! });
      return;
    }
    throwIf(parts.length == 0, "cannot delete the root folder");
    // not from memory: what we remove must be the folder at this path now
    this.dirIDs.clear();
    let parent = await this.parentOf(parts, false);
    let f = findItem(parent.items, parts[parts.length - 1], true);
    throwIf(!f, `folder not found: ${path}`);
    try {
      await pmCall(this.key, "POST", "/folder/delete", { id: f!.id! });
    } finally {
      this.dirIDs.clear();
    }
  }
}

function isHttps(s: string): boolean {
  try {
    return new URL(s).protocol == "https:";
  } catch {
    return false;
  }
}

function seconds(v: number | undefined): number {
  let n = Number(v || 0);
  return Number.isFinite(n) && n > 0 ? n * 1000 : 0;
}

export function premiumizeItemsToWire(items: PremiumizeItem[]): any[][] {
  let wires: any[][] = [];
  for (let it of items) {
    if (!it.id || !it.name) {
      continue;
    }
    if (isFolder(it)) {
      wires.push([it.id, it.name, -1, seconds(it.created_at), []]);
      continue;
    }
    if (it.type != "file") {
      continue;
    }
    let size = Number(it.size || 0);
    let meta: any[] = [it.id, it.name, Number.isFinite(size) ? size : 0, seconds(it.created_at), null];
    if (it.mime_type) {
      meta.push(prop_premiumize_mime_type, it.mime_type);
    }
    wires.push(meta);
  }
  return wires;
}

/**
 * Checks the API key. Returns the customer id of its account.
 */
export async function premiumizeConnect(apiKey: string): Promise<string> {
  let js = await pmCall(apiKey, "POST", "/account/info", {});
  throwIf(!js.customer_id, "premiumize.me did not return the account");
  return String(js.customer_id);
}

function parseJSON(text: string): any {
  try {
    let js = JSON.parse(text);
    return js && typeof js == "object" ? js : {};
  } catch {
    return {};
  }
}

// premiumize.me answers 200 for errors too: the status is in the answer.
function pmCheck(httpStatus: number, js: any): any {
  if (httpStatus >= 200 && httpStatus < 300 && js.status == "success") {
    return js;
  }
  let msg = typeof js.message == "string" ? js.message.trim().substring(0, 200) : "";
  let auth = js.code == "authentication_failed" || httpStatus == 401 || /not logged in/i.test(msg);
  if (auth) {
    throw new PremiumizeError("premiumize.me refused the API key", true);
  }
  throw new PremiumizeError(msg ? `premiumize.me: ${msg}` : `premiumize.me ${httpStatus}`, false);
}

async function pmCall(apiKey: string, method: string, path: string, params: Record<string, string>): Promise<any> {
  let query = new URLSearchParams({ apikey: apiKey });
  let init: RequestInit = { method };
  if (method == "GET") {
    for (let [k, v] of Object.entries(params)) {
      query.set(k, v);
    }
  } else {
    // a form: a JSON body would need the API to allow its header
    let form = new FormData();
    for (let [k, v] of Object.entries(params)) {
      form.append(k, v);
    }
    init.body = form;
  }
  let rsp: Response;
  try {
    rsp = await fetch(`${premiumizeAPI}${path}?${query}`, init);
  } catch {
    // the default message would quote the address, which carries the key
    throw new Error("could not connect to premiumize.me");
  }
  return pmCheck(rsp.status, parseJSON(await rsp.text()));
}

// Posts a file to an upload server. XMLHttpRequest because fetch does not
// report how much was sent.
function pmSendFile(url: string, form: FormData, onProgress: (loaded: number, durMs: number) => void): Promise<any> {
  return new Promise((resolve, reject) => {
    let started = Date.now();
    let xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.responseType = "text";
    xhr.onload = () => {
      try {
        resolve(pmCheck(xhr.status, parseJSON(xhr.responseText)));
      } catch (e) {
        reject(e);
      }
    };
    xhr.onerror = () => {
      reject(new Error("upload to premiumize.me failed"));
    };
    xhr.upload.onprogress = (ev: ProgressEvent) => {
      onProgress(ev.loaded, Date.now() - started);
    };
    xhr.send(form);
  });
}

export function makeFileSystemPremiumize(c: PremiumizeConfig): FileSystemPremiumize {
  return new FileSystemPremiumize(c);
}
